import React, { useCallback, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { Block, Delete } from '@material-ui/icons'
import { FlexHorizontal, LoadMask, Spacer, useToasts } from '@vestaboard/installables'
import { useCreatePromotionCode } from '../hooks/useCreatePromotionCode'
import { useListPromotionCodes, PlusPromotionCodeItem } from '../hooks/useListPromotionCodes'
import { useDeactivatePromotionCode } from '../hooks/useDeactivatePromotionCode'
import { useDeletePromotionCode } from '../hooks/useDeletePromotionCode'

const useStyles = makeStyles({
  formCard: {
    padding: 24,
  },
  formRow: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  codeInput: {
    minWidth: 200,
  },
  percentInput: {
    width: 140,
  },
  numberInput: {
    width: 160,
  },
  activeChip: {
    backgroundColor: '#4caf50',
    color: '#fff',
  },
  inactiveChip: {
    backgroundColor: '#9e9e9e',
    color: '#fff',
  },
})

const formatDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
})

export const PromotionCodes = () => {
  const classes = useStyles()
  const { addToast } = useToasts()
  const { data, loading, error, refetch } = useListPromotionCodes()
  const [createMutation, { loading: creating }] = useCreatePromotionCode()
  const [deactivateMutation] = useDeactivatePromotionCode()
  const [deleteMutation] = useDeletePromotionCode()

  const [code, setCode] = useState('')
  const [percentOff, setPercentOff] = useState<number>(10)
  const [maxRedemptions, setMaxRedemptions] = useState<string>('')
  const [expiresAt, setExpiresAt] = useState<string>('')
  const [deactivateTarget, setDeactivateTarget] = useState<PlusPromotionCodeItem | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PlusPromotionCodeItem | null>(null)

  const handleCreate = useCallback(async () => {
    if (!code.trim()) {
      addToast('Code is required', { appearance: 'error' })
      return
    }
    if (percentOff < 1 || percentOff > 100) {
      addToast('Percent off must be between 1 and 100', { appearance: 'error' })
      return
    }

    try {
      const { data: result } = await createMutation({
        variables: {
          input: {
            code: code.trim(),
            percentOff,
            maxRedemptions: maxRedemptions ? parseInt(maxRedemptions, 10) : null,
            expiresAt: expiresAt ? (() => {
              const [y, m, d] = expiresAt.split('-').map(Number)
              return new Date(y, m - 1, d, 23, 59, 59).getTime()
            })() : null,
          },
        },
      })

      const response = result?.createPlusPromotionCodeV2
      if (response && 'error' in response) {
        addToast(response.error, { appearance: 'error' })
        return
      }

      addToast(`Promotion code ${code.toUpperCase()} created`, { appearance: 'success' })
      setCode('')
      setPercentOff(10)
      setMaxRedemptions('')
      setExpiresAt('')
      refetch()
    } catch (e: any) {
      addToast(e.message || 'Error creating promotion code', { appearance: 'error' })
    }
  }, [code, percentOff, maxRedemptions, expiresAt, createMutation, addToast, refetch])

  const handleDeactivate = useCallback(async () => {
    if (!deactivateTarget) return

    try {
      const { data: result } = await deactivateMutation({
        variables: { id: deactivateTarget.id },
      })

      const response = result?.deactivatePlusPromotionCodeV2
      if (response && 'error' in response) {
        addToast(response.error, { appearance: 'error' })
        return
      }

      addToast(`Promotion code ${deactivateTarget.code} deactivated`, { appearance: 'success' })
      refetch()
    } catch (e: any) {
      addToast(e.message || 'Error deactivating promotion code', { appearance: 'error' })
    } finally {
      setDeactivateTarget(null)
    }
  }, [deactivateTarget, deactivateMutation, addToast, refetch])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return

    try {
      const { data: result } = await deleteMutation({
        variables: { id: deleteTarget.id },
      })

      const response = result?.deletePlusPromotionCodeV2
      if (response && 'error' in response) {
        addToast(response.error, { appearance: 'error' })
        return
      }

      addToast(`Promotion code ${deleteTarget.code} deleted`, { appearance: 'success' })
      refetch()
    } catch (e: any) {
      addToast(e.message || 'Error deleting promotion code', { appearance: 'error' })
    } finally {
      setDeleteTarget(null)
    }
  }, [deleteTarget, deleteMutation, addToast, refetch])

  const promotionCodes = data?.listPlusPromotionCodesV2 ?? []

  return (
    <>
      {(loading || creating) && <LoadMask />}
      <FlexHorizontal spaceBetween>
        <Typography variant='h4'>V+ Stripe Promotion Codes</Typography>
      </FlexHorizontal>
      <Typography variant='body2' color='textSecondary'>
        Manage Stripe promotion codes for Vestaboard+ subscriptions. These codes apply discounts at checkout.
      </Typography>
      <Spacer size='extraLarge' />

      <Card>
        <Box className={classes.formCard}>
          <Typography variant='h6'>Create Stripe Promotion Code</Typography>
          <Spacer size='medium' />
          <Box className={classes.formRow}>
            <TextField
              className={classes.codeInput}
              variant='outlined'
              label='Code'
              placeholder='e.g. INFLUENCER20'
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              size='small'
            />
            <TextField
              className={classes.percentInput}
              variant='outlined'
              label='% Off'
              type='number'
              value={percentOff}
              onChange={e => setPercentOff(Math.min(100, Math.max(0, +e.target.value)))}
              size='small'
              inputProps={{ min: 1, max: 100 }}
            />
            <TextField
              className={classes.numberInput}
              variant='outlined'
              label='Max Uses'
              type='number'
              placeholder='Unlimited'
              value={maxRedemptions}
              onChange={e => setMaxRedemptions(e.target.value)}
              size='small'
              inputProps={{ min: 1 }}
            />
            <TextField
              className={classes.numberInput}
              variant='outlined'
              label='Expires'
              type='date'
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
              size='small'
              InputLabelProps={{ shrink: true }}
            />
            <Button variant='contained' color='primary' onClick={handleCreate} disabled={creating}>
              Create
            </Button>
          </Box>
        </Box>
      </Card>

      <Spacer size='extraLarge' />

      {promotionCodes.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Redeemed</TableCell>
                <TableCell>Max Uses</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {promotionCodes.map(pc => (
                <TableRow key={pc.id}>
                  <TableCell>
                    <Typography variant='body2' style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {pc.code}
                    </Typography>
                  </TableCell>
                  <TableCell>{pc.percentOff}% off</TableCell>
                  <TableCell>
                    <Chip
                      label={pc.active ? 'Active' : 'Inactive'}
                      size='small'
                      className={pc.active ? classes.activeChip : classes.inactiveChip}
                    />
                  </TableCell>
                  <TableCell>{pc.timesRedeemed}</TableCell>
                  <TableCell>{pc.maxRedemptions ?? '∞'}</TableCell>
                  <TableCell>{pc.expiresAt ? formatDate(pc.expiresAt) : '—'}</TableCell>
                  <TableCell>{formatDate(pc.createdAt)}</TableCell>
                  <TableCell>
                    {pc.active && (
                      <IconButton size='small' onClick={() => setDeactivateTarget(pc)} title='Deactivate'>
                        <Block fontSize='small' />
                      </IconButton>
                    )}
                    <IconButton size='small' onClick={() => setDeleteTarget(pc)} title='Delete' style={{ color: '#f44336' }}>
                      <Delete fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && error && (
        <Typography variant='body1' color='error'>
          Failed to load promotion codes. Please try again.
        </Typography>
      )}

      {!loading && !error && promotionCodes.length === 0 && (
        <Typography variant='body1' color='textSecondary'>
          No promotion codes yet. Create one above.
        </Typography>
      )}

      <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)}>
        <DialogTitle>Deactivate Promotion Code</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate <strong>{deactivateTarget?.code}</strong>? This code will no longer be
            usable at checkout.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateTarget(null)}>Cancel</Button>
          <Button onClick={handleDeactivate} color='secondary' variant='contained'>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Promotion Code</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{deleteTarget?.code}</strong>? This will deactivate the
            promotion code and delete the underlying Stripe coupon. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDelete} style={{ backgroundColor: '#f44336', color: '#fff' }} variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
